/**
 * @fileoverview Formulário para criação e edição de promoções.
 * Componente reutilizável que suporta modo criação e edição.
 * 
 * @module PromotionForm
 * @requires react
 * @requires @mui/material
 * @requires ../../services/promotionsApi
 * @since 1.0.0
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  OutlinedInput,
  FormControlLabel,
  Checkbox,
  Grid,
  Divider,
  Typography,
} from '@mui/material';
import { Inventory as InventoryIcon } from '@mui/icons-material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { createPromotion, updatePromotion } from '../../services/promotionsApi';
import TargetManager from './TargetManager';

/**
 * Formulário de promoções.
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Se o modal está aberto
 * @param {Function} props.onClose - Callback para fechar modal
 * @param {string} props.mode - Modo: 'create' ou 'edit'
 * @param {Object} props.promotion - Dados da promoção (para edição)
 * @param {Function} props.onSuccess - Callback de sucesso
 * @param {Function} props.onError - Callback de erro
 * @returns {JSX.Element}
 */
export default function PromotionForm({ open, onClose, mode, promotion, onSuccess, onError }) {
  const [formData, setFormData] = useState({
    name: '',
    status: 'draft',
    period: {
      initial_date: '',
      final_date: '',
      time_of_day: {
        start: '00:00',
        end: '23:59'
      }
    },
    recurrence: {
      type: 'none',
      days_of_week: [],
      day_of_month: null,
      month: null
    },
    context: {
      season: null,
      kind: 'random',
      holiday: null,
      tags: [],
      description: ''
    },
    targets: {
      products: [],
      categories: [],
      brands: [],
      exclude_products: [],
      exclude_categories: []
    },
    discount: {
      type: 'percentage',
      value: '',
      currency: 'BRL',
      max_discount_amount: null,
      min_order_value: null,
      min_quantity: 1,
      buy_x_get_y: null
    },
    conditions: {
      customer_segments: ['all'],
      channels: ['web'],
      coupon_code: null,
      stackable: false,
      usage_limit_total: null,
      usage_limit_per_user: null
    }
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [targetManagerOpen, setTargetManagerOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);

  useEffect(() => {
    if (open && mode === 'edit' && promotion) {
      setFormData(promotion);
      setImagePreview(promotion.context?.image_url || null);
      setImageBase64(null);
      setImageRemoved(false);
    } else if (open && mode === 'create') {
      setImagePreview(null);
      setImageBase64(null);
      setImageRemoved(false);
      setFormData({
        name: '',
        status: 'draft',
        period: {
          initial_date: '',
          final_date: '',
          time_of_day: {
            start: '00:00',
            end: '23:59'
          }
        },
        recurrence: {
          type: 'none',
          days_of_week: [],
          day_of_month: null,
          month: null
        },
        context: {
          season: null,
          kind: 'random',
          holiday: null,
          tags: [],
          description: ''
        },
        targets: {
          products: [],
          categories: [],
          brands: [],
          exclude_products: [],
          exclude_categories: []
        },
        discount: {
          type: 'percentage',
          value: 0,
          currency: 'BRL',
          max_discount_amount: null,
          min_order_value: null,
          min_quantity: 1,
          buy_x_get_y: null
        },
        conditions: {
          customer_segments: ['all'],
          channels: ['web'],
          coupon_code: null,
          stackable: false,
          usage_limit_total: null,
          usage_limit_per_user: null
        }
      });
    }
    setErrors({});
  }, [open, mode, promotion]);

  /**
   * Manipula seleção de imagem da promoção.
   * @function handleImageChange
   * @param {Event} e - Evento de input file
   */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      setErrors((prev) => ({ ...prev, image: 'Formato inválido. Use PNG, JPEG, JPG ou WEBP' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, image: 'Imagem muito grande. Máximo: 5MB' }));
      return;
    }

    setErrors((prev) => { const { image, ...rest } = prev; return rest; });

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setImageBase64(reader.result);
      setImageRemoved(false);
    };
    reader.readAsDataURL(file);
  };

  /**
   * Remove a imagem selecionada.
   */
  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageBase64(null);
    setImageRemoved(true);
  };

  /**
   * Valida os campos do formulário.
   * @function validateForm
   * @returns {boolean} True se válido
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.period.initial_date) {
      newErrors.initial_date = 'Data inicial é obrigatória';
    }

    if (!formData.period.final_date) {
      newErrors.final_date = 'Data final é obrigatória';
    }

    if (formData.period.initial_date && formData.period.final_date) {
      if (new Date(formData.period.initial_date) > new Date(formData.period.final_date)) {
        newErrors.final_date = 'Data final deve ser maior que a inicial';
      }
    }

    const discountValue = parseFloat(formData.discount.value);
    if (!formData.discount.value || isNaN(discountValue) || discountValue <= 0) {
      newErrors.discount_value = 'Valor do desconto deve ser maior que zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Converte targets para formato de envio (apenas IDs).
   * @function convertTargetsToIds
   * @param {Object} targets - Targets com objetos {id, name}
   * @returns {Object} Targets apenas com IDs
   */
  const convertTargetsToIds = (targets) => {
    const converted = {
      products: [],
      categories: [],
      brands: [],
      exclude_products: targets.exclude_products || [],
      exclude_categories: targets.exclude_categories || []
    };
    
    ['products', 'categories', 'brands'].forEach(key => {
      if (targets[key]) {
        converted[key] = targets[key].map(item => 
          typeof item === 'object' ? item.id : item
        );
      }
    });
    
    return converted;
  };

  /**
   * Submete o formulário.
   * @async
   * @function handleSubmit
   * @returns {Promise<void>}
   */
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const submitData = {
        ...formData,
        context_image: imageBase64 || undefined,
        remove_image: imageRemoved || undefined,
        targets: convertTargetsToIds(formData.targets),
        discount: {
          ...formData.discount,
          value: parseFloat(formData.discount.value) || 0
        }
      };
      
      if (mode === 'create') {
        await createPromotion(submitData);
      } else {
        await updatePromotion(promotion._id || promotion.id, submitData);
      }
      
      onSuccess();
    } catch (error) {
      onError(error.message || 'Erro ao salvar promoção');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Atualiza campo aninhado do formulário.
   * @function updateNestedField
   * @param {string} path - Caminho do campo (ex: 'period.initial_date')
   * @param {any} value - Novo valor
   * @returns {void}
   */
  const updateNestedField = (path, value) => {
    const keys = path.split('.');
    const newData = { ...formData };
    let current = newData;
    
    for (let i = 0; i < keys.length - 1; i++) {
      current = current[keys[i]];
    }
    
    current[keys[keys.length - 1]] = value;
    setFormData(newData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === 'create' ? 'Nova Promoção' : 'Editar Promoção'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          
          {/* Informações Básicas */}
          <Box>
            <Typography variant="h6" gutterBottom>Informações Básicas</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nome da Promoção *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  error={!!errors.name}
                  helperText={errors.name}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="draft">Rascunho</MenuItem>
                    <MenuItem value="scheduled">Agendada</MenuItem>
                    <MenuItem value="active">Ativa</MenuItem>
                    <MenuItem value="expired">Expirada</MenuItem>
                    <MenuItem value="canceled">Cancelada</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Período */}
          <Box>
            <Typography variant="h6" gutterBottom>Período</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Data Inicial *"
                  type="date"
                  value={formData.period.initial_date}
                  onChange={(e) => updateNestedField('period.initial_date', e.target.value)}
                  error={!!errors.initial_date}
                  helperText={errors.initial_date}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Data Final *"
                  type="date"
                  value={formData.period.final_date}
                  onChange={(e) => updateNestedField('period.final_date', e.target.value)}
                  error={!!errors.final_date}
                  helperText={errors.final_date}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Hora Início"
                  type="time"
                  value={formData.period.time_of_day.start}
                  onChange={(e) => updateNestedField('period.time_of_day.start', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label="Hora Fim"
                  type="time"
                  value={formData.period.time_of_day.end}
                  onChange={(e) => updateNestedField('period.time_of_day.end', e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Desconto */}
          <Box>
            <Typography variant="h6" gutterBottom>Desconto</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Desconto</InputLabel>
                  <Select
                    value={formData.discount.type}
                    onChange={(e) => updateNestedField('discount.type', e.target.value)}
                    label="Tipo de Desconto"
                  >
                    <MenuItem value="percentage">Percentual</MenuItem>
                    <MenuItem value="fixed">Valor Fixo</MenuItem>
                    <MenuItem value="bxgy">Compre X Leve Y</MenuItem>
                    <MenuItem value="bundle">Pacote</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Valor *"
                  type="number"
                  value={formData.discount.value}
                  onChange={(e) => updateNestedField('discount.value', e.target.value)}
                  error={!!errors.discount_value}
                  helperText={errors.discount_value}
                  placeholder="0.0"
                  inputProps={{
                    step: '0.01',
                    min: '0'
                  }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Moeda"
                  value={formData.discount.currency}
                  onChange={(e) => updateNestedField('discount.currency', e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Contexto */}
          <Box>
            <Typography variant="h6" gutterBottom>Contexto</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Estação</InputLabel>
                  <Select
                    value={formData.context.season || ''}
                    onChange={(e) => updateNestedField('context.season', e.target.value || null)}
                    label="Estação"
                  >
                    <MenuItem value="">Nenhuma</MenuItem>
                    <MenuItem value="summer">Verão</MenuItem>
                    <MenuItem value="autumn">Outono</MenuItem>
                    <MenuItem value="winter">Inverno</MenuItem>
                    <MenuItem value="spring">Primavera</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={formData.context.kind}
                    onChange={(e) => updateNestedField('context.kind', e.target.value)}
                    label="Tipo"
                  >
                    <MenuItem value="seasonal">Sazonal</MenuItem>
                    <MenuItem value="holiday">Feriado</MenuItem>
                    <MenuItem value="periodic">Periódica</MenuItem>
                    <MenuItem value="random">Aleatória</MenuItem>
                    <MenuItem value="clearance">Liquidação</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Descrição"
                  value={formData.context.description}
                  onChange={(e) => updateNestedField('context.description', e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Imagem da Promoção</Typography>
                {errors.image && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                    {errors.image}
                  </Typography>
                )}
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    p: 2,
                    textAlign: 'center',
                    minHeight: 160,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  {imagePreview ? (
                    <Box
                      component="img"
                      src={imagePreview}
                      alt="Preview"
                      sx={{ maxWidth: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 1 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <PhotoCameraIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                  )}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <label htmlFor="promotion-image-upload">
                      <input
                        id="promotion-image-upload"
                        key={imagePreview || 'empty'}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        style={{ display: 'none' }}
                        onChange={handleImageChange}
                        onClick={(e) => { e.target.value = null; }}
                      />
                      <Button variant="outlined" component="span" size="small" startIcon={<PhotoCameraIcon />}>
                        {imagePreview ? 'Trocar imagem' : 'Selecionar imagem'}
                      </Button>
                    </label>
                    {imagePreview && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleRemoveImage}
                      >
                        Remover
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* Targets */}
          <Box>
            <Typography variant="h6" gutterBottom>Itens da Promoção</Typography>
            <Button
              variant="outlined"
              startIcon={<InventoryIcon />}
              onClick={() => setTargetManagerOpen(true)}
            >
              Gerenciar Itens ({
                (formData.targets.products?.length || 0) +
                (formData.targets.categories?.length || 0) +
                (formData.targets.brands?.length || 0)
              })
            </Button>
          </Box>

          <Divider />

          {/* Condições */}
          <Box>
            <Typography variant="h6" gutterBottom>Condições</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Canais</InputLabel>
                  <Select
                    multiple
                    value={formData.conditions.channels}
                    onChange={(e) => updateNestedField('conditions.channels', e.target.value)}
                    input={<OutlinedInput label="Canais" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    )}
                  >
                    <MenuItem value="web">Web</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Código do Cupom"
                  value={formData.conditions.coupon_code || ''}
                  onChange={(e) => updateNestedField('conditions.coupon_code', e.target.value || null)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.conditions.stackable}
                      onChange={(e) => updateNestedField('conditions.stackable', e.target.checked)}
                    />
                  }
                  label="Pode ser combinada com outras promoções"
                />
              </Grid>
            </Grid>
          </Box>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Salvando...' : (mode === 'create' ? 'Criar' : 'Atualizar')}
        </Button>
      </DialogActions>

      <TargetManager
        open={targetManagerOpen}
        onClose={() => setTargetManagerOpen(false)}
        targets={formData.targets}
        onSave={(newTargets) => {
          setFormData({ ...formData, targets: newTargets });
          setTargetManagerOpen(false);
        }}
      />
    </Dialog>
  );
}